import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Snackbar,
  Fade,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  RateReview as RateReviewIcon,
  CheckCircle as CheckCircleIcon,
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDashboardMetrics } from '../hooks/useDashboardMetrics';
import AnimatedProgress from '../components/dashboard/AnimatedProgress';

// Import sub-components
import ProcessingMetrics from '../components/dashboard/ProcessingMetrics';
import ReviewMetrics from '../components/dashboard/ReviewMetrics';
import DocumentMetricsList from '../components/dashboard/DocumentMetricsList';
import DifficultyDistribution from '../components/dashboard/DifficultyDistribution';
import SuggestedTopicsMetrics from '../components/dashboard/SuggestedTopicsMetrics';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function TutorDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [updateMessage, setUpdateMessage] = useState('');

  // Use the custom hook for metrics management
  const {
    metrics,
    loading,
    error,
    lastUpdated,
    isRefreshing,
    refresh,
    getMetricChanges,
  } = useDashboardMetrics(30000); // 30 second refresh interval

  // Check for metric changes and show notifications
  useEffect(() => {
    const changes = getMetricChanges();
    if (changes) {
      const hasProcessingChanges = Object.keys(changes.processing).length > 0;
      const hasReviewChanges = Object.keys(changes.review).length > 0;
      
      if (hasProcessingChanges || hasReviewChanges) {
        let message = 'Dashboard updated: ';
        const updates = [];
        
        if (hasProcessingChanges) {
          const total = Object.values(changes.processing).reduce((sum, val) => sum + val, 0);
          updates.push(`${total} new segments processed`);
        }
        
        if (hasReviewChanges) {
          const total = Object.values(changes.review).reduce((sum, val) => sum + val, 0);
          updates.push(`${total} new reviews completed`);
        }
        
        setUpdateMessage(message + updates.join(', '));
        setShowUpdateNotification(true);
      }
    }
  }, [metrics, getMetricChanges]);

  const handleRefresh = () => {
    refresh();
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const navigateToProcessing = () => {
    navigate('/segments'); // Navigate to segments page for processing
  };

  const navigateToReview = () => {
    navigate('/complete-review'); // Navigate to complete review page
  };

  if (loading && !metrics) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !metrics) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h4" component="h1" gutterBottom>
              Tutor Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Welcome back, {user?.first_name || user?.username}! Here's your exam processing overview.
            </Typography>
          </Grid>
          <Grid item>
            <Box display="flex" alignItems="center" gap={1}>
              {isRefreshing && (
                <Fade in={isRefreshing}>
                  <CircularProgress size={20} />
                </Fade>
              )}
              <Tooltip title={lastUpdated ? `Last updated: ${lastUpdated.toLocaleTimeString()}` : 'Refresh metrics'}>
                <IconButton onClick={handleRefresh} disabled={isRefreshing}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Quick Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Segments
                  </Typography>
                  <Typography variant="h4">
                    {metrics?.processing?.total_segments || 0}
                  </Typography>
                </Box>
                <AssignmentIcon sx={{ fontSize: 40, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Processing Progress
                  </Typography>
                  <Typography variant="h4">
                    {metrics?.processing?.latex_formatter?.percentage || 0}%
                  </Typography>
                </Box>
                <TimelineIcon sx={{ fontSize: 40, color: 'info.main', opacity: 0.3 }} />
              </Box>
              <AnimatedProgress 
                value={metrics?.processing?.latex_formatter?.percentage || 0} 
                height={8}
                color="info"
                animate={true}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Review Progress
                  </Typography>
                  <Typography variant="h4">
                    {metrics?.review?.fully_verified?.percentage || 0}%
                  </Typography>
                </Box>
                <RateReviewIcon sx={{ fontSize: 40, color: 'warning.main', opacity: 0.3 }} />
              </Box>
              <AnimatedProgress 
                value={metrics?.review?.fully_verified?.percentage || 0} 
                height={8}
                color="warning"
                animate={true}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Completed
                  </Typography>
                  <Typography variant="h4">
                    {metrics?.review?.fully_verified?.count || 0}
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item>
            <Button
              variant="contained"
              startIcon={<TimelineIcon />}
              onClick={navigateToProcessing}
            >
              Go to Processing
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RateReviewIcon />}
              onClick={navigateToReview}
            >
              Go to Review
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Tabs for Different Views */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="dashboard tabs">
          <Tab label="Overall Statistics" />
          <Tab label="Document Breakdown" />
          <Tab label="Difficulty Distribution" />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <ProcessingMetrics metrics={metrics?.processing} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <ReviewMetrics metrics={metrics?.review} />
          </Grid>
          {metrics?.suggested_topics && metrics.suggested_topics.length > 0 && (
            <Grid item xs={12}>
              <SuggestedTopicsMetrics suggestedTopics={metrics.suggested_topics} />
            </Grid>
          )}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <DocumentMetricsList />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <DifficultyDistribution />
      </TabPanel>

      {/* Update Notification */}
      <Snackbar
        open={showUpdateNotification}
        autoHideDuration={6000}
        onClose={() => setShowUpdateNotification(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setShowUpdateNotification(false)} 
          severity="info" 
          sx={{ width: '100%' }}
          icon={<TrendingUpIcon />}
        >
          {updateMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}